import { useEffect, useRef, useState } from 'react';

export type UseCollapseOnOverflowOptions = {
  /**
   * Force collapsed state regardless of overflow (e.g., below a breakpoint)
   */
  forceCollapse?: boolean;
  /**
   * Also observe children for size changes (e.g., dynamic badges)
   */
  observeChildren?: boolean;
  /**
   * Optional external constraint element whose width should be used as the available width
   * for overflow calculation (e.g., the drawer container). If not provided, the measured
   * element's own clientWidth is used.
   */
  constraintRef?: React.RefObject<HTMLElement>;
};

export type UseCollapseOnOverflowReturn<T extends HTMLElement> = {
  /** Attach to the container that wraps the tabs list */
  ref: React.RefObject<T>;
  /** True when content overflows or when forceCollapse is set */
  collapsed: boolean;
  /** Manually trigger a re-check if needed */
  recheck: () => void;
};

/**
 * Detects when the horizontal content of a container overflows its width
 * and returns a boolean to collapse UI (e.g., switch tabs → dropdown).
 *
 * SSR safe. Uses ResizeObserver when available.
 */
export function useCollapseOnOverflow<T extends HTMLElement>(
  options: UseCollapseOnOverflowOptions = {}
): UseCollapseOnOverflowReturn<T> {
  const containerRef = useRef<T>(null);
  const [overflowing, setOverflowing] = useState(false);

  const checkOverflow = () => {
    const el = containerRef.current as HTMLElement | null;
    if (!el) return;
    const constraintEl = options.constraintRef?.current as HTMLElement | null;
    // Calculate available width:
    // - If a constraint element is provided (e.g., drawer), use its CONTENT width (clientWidth minus paddings)
    // - Otherwise use the measured element's width
    // - Finally, take the narrower of the two to be safe when nested layouts/paddings apply
    let constraintContentWidth: number | null = null;
    if (constraintEl) {
      try {
        const styles = window.getComputedStyle(constraintEl);
        const paddingLeft = parseFloat(styles.paddingLeft || '0') || 0;
        const paddingRight = parseFloat(styles.paddingRight || '0') || 0;
        constraintContentWidth = Math.max(0, constraintEl.clientWidth - paddingLeft - paddingRight);
      } catch {
        // Fallback if getComputedStyle fails for any reason
        constraintContentWidth = constraintEl.clientWidth;
      }
    }
    const selfWidth = el.clientWidth;
    const availableWidth = Math.min(constraintContentWidth ?? Number.POSITIVE_INFINITY, selfWidth);
    const contentWidth = el.scrollWidth;
    const hasOverflow = contentWidth > availableWidth;
    setOverflowing(hasOverflow);
  };

  useEffect(() => {
    const el = containerRef.current as HTMLElement | null;
    if (!el) return;
    if (typeof window === 'undefined') return;

    let raf = 0;
    const onResize = () => {
      cancelAnimationFrame(raf);
      raf = window.requestAnimationFrame(checkOverflow);
    };

    // Initial check
    checkOverflow();

    // Observe element size changes
    let ro: ResizeObserver | null = null;
    if (typeof (window as any).ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(onResize);
      ro.observe(el);
      // If a constraint element is provided, observe it as well so collapsing reacts
      // to container (e.g., drawer) resizes.
      if (options.constraintRef?.current) {
        ro.observe(options.constraintRef.current);
      }
      if (options.observeChildren) {
        Array.from(el.children).forEach((child) => ro!.observe(child as Element));
      }
    }

    // Always listen to window resizes as a backstop (DevTools device toolbar, DPI changes, etc.)
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize as any);

    return () => {
      cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize as any);
    };
    // It's intentional not to depend on el; ref target is stable for our use.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.observeChildren, options.constraintRef?.current]);

  const collapsed = options.forceCollapse || overflowing;

  return { ref: containerRef, collapsed, recheck: checkOverflow };
}
